import { feedbackRead } from "components/Analytics/feedbackRead";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Alert, Button, FormGroup, Input, Label, Spinner } from "reactstrap";
import FeedbackBreakdown from "components/Analytics/FeedbackBreakdown";
import FeedbackCohorts from "components/Analytics/FeedbackCohorts";
import FeedbackAccuracy from "components/Analytics/FeedbackAccuracy";
import FeedbackCompare from "components/Analytics/FeedbackCompare";
import FeedbackLatest from "components/Analytics/FeedbackLatest";
import FeedbackRatingsModal, { reasonLabel, sourceLabel } from "components/Analytics/FeedbackRatingsModal";
import FeedbackThemes from "components/Analytics/FeedbackThemes";
import FeedbackTrends from "components/Analytics/FeedbackTrends";
import "./NoteQualityDashboard.css";
import "./FeedbackDashboard.css";

// On unless turned off (FRONTEND_FEATURE_DEFAULTS / User.py), and ratings only
// store flags set on the user, so their "off" group is mostly on.
const DEFAULT_ON_FLAGS = ["get_all_notes", "user_note_rules", "show_multi_recorder"];

const PRESETS = [
  { key: "today", label: "Today", days: 1 },
  { key: "7", label: "7 days", days: 7 },
  { key: "30", label: "30 days", days: 30 },
  { key: "90", label: "90 days", days: 90 },
];

const isoDate = (date) => {
  const offset = date.getTimezoneOffset() * 60000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 10);
};

// How the rated visit was recorded (comprehend_feedback.capture_mode).
export const MULTI_RECORD_OPTIONS = [
  { value: "any", label: "Any multi-record" },
  { value: "segmented", label: "Multi-record with error prevention" },
  { value: "classic", label: "Classic multi-record" },
  { value: "none", label: "Not multi-record" },
];

const buildRequest = ({ range, emr, noteType, clinicId, emails, flag, flagState, source, multiRecord }) => {
  const userIds = [...new Set(emails.split(/[,\n]/).map((email) => email.trim().toLowerCase()).filter(Boolean))];
  const body = { ...range, userIds };
  if (emr) body.emr = emr;
  if (noteType) body.noteType = noteType;
  if (clinicId) body.clinicId = clinicId;
  if (flag) { body.flag = flag; body.flagState = flagState; }
  if (source) body.source = source;
  if (multiRecord) body.multiRecord = multiRecord;
  return body;
};

const presetRange = (days) => {
  const end = new Date();
  const start = new Date(end);
  start.setDate(end.getDate() - (days - 1));
  return { startDate: isoDate(start), endDate: isoDate(end) };
};

const shortDate = (value) =>
  new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const pct = (value) => (value == null ? "—" : `${Number(value).toFixed(value >= 10 || Number.isInteger(value) ? 0 : 1)}%`);
const points = (value) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${Math.abs(value).toFixed(1)} pts`;
const count = (value) => Number(value || 0).toLocaleString();

const options = (rows = []) => rows.map((row) => ({ value: row.value, label: row.label || row.value }));

function considerationText(item) {
  switch (item.kind) {
    case "lowCoverage":
      return ["Heads up", `Only ${pct(item.coverage)} of notes were rated. PTs who rate tend to be the unhappy ones, so the thumbs-down rate probably runs higher than reality.`];
    case "vocalClinician":
      return ["Heads up", `${item.userId} gave ${pct(item.pct)} of all thumbs downs here. One vocal clinician can skew the reasons and themes.`];
    case "dominantEmr":
      return ["Keep in mind", `${item.emr} makes up ${pct(item.pct)} of ratings, so the overall numbers lean toward how ${item.emr} is doing.`];
    case "smallEmr":
      return ["Heads up", `${item.emr} has only ${count(item.users)} active ${item.users === 1 ? "user" : "users"} (${pct(item.pct)} of users). Its numbers can swing a lot, so weigh it less than bigger EMRs.`];
    case "comments":
      return ["Keep in mind", `Quotes only come from PTs who wrote a comment (${count(item.comments)} here). Quieter PTs may have different problems.`];
    case "flagFilter":
      return ["Keep in mind", "Feature on/off comparisons aren't random. Clinicians who turned a feature on may already differ from those who didn't, so treat it as a hint, not proof."];
    case "flagDefaultOn":
      return ["Heads up", `${item.flag} is on by default, and only flags set on a user are saved. "Off" here means "not set", which is mostly users who have it on.`];
    default:
      return null;
  }
}

function TrendCard({ card, onLook }) {
  const trending = card.kind === "trending";
  return (
    <article className={`fb-trend-card ${trending ? "fb-trend-up" : "fb-trend-higher"}`}>
      <span className="fb-badge">{trending ? "↑ Trending up" : "Higher than other EMRs"}</span>
      <h4>{card.section} on {card.emr}</h4>
      <p>
        {trending
          ? <>Thumbs downs flagging {card.section} went from {pct(card.rateBefore)} to {pct(card.rateNow)} of rated notes <strong>({points(card.change)})</strong>.</>
          : <>{pct(card.rateNow)} of rated notes get a thumbs down flagging {card.section}, vs {pct(card.rateOthers)} on other EMRs.</>}
      </p>
      {card.topReason && <p className="fb-mostly">Mostly: <strong>{reasonLabel(card.topReason.reason)} ({pct(card.topReason.pct)})</strong></p>}
      <p className="fb-card-meta">
        {card.downsPerWeek.toLocaleString()} thumbs downs a week · {count(card.users)} {card.users === 1 ? "user" : "users"} on {card.emr}
        {card.smallGroup && <span className="fb-small-group"> (small group)</span>}
      </p>
      {card.quote && <blockquote className="fb-quote">“{card.quote}”</blockquote>}
      <Button color="link" className="fb-link" onClick={onLook}>Go look ›</Button>
    </article>
  );
}

const FeedbackDashboard = ({ apiClient }) => {
  const [preset, setPreset] = useState("90");
  const [range, setRange] = useState(() => presetRange(90));
  const [emr, setEmr] = useState("");
  const [noteType, setNoteType] = useState("");
  const [clinicId, setClinicId] = useState("");
  const [emails, setEmails] = useState("");
  const [emailFilterOpen, setEmailFilterOpen] = useState(false);
  const emailFilterRef = useRef(null);
  const [flag, setFlag] = useState("");
  const [flagState, setFlagState] = useState("on");
  const [source, setSource] = useState("");
  const [multiRecord, setMultiRecord] = useState("");
  const [overview, setOverview] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [retry, setRetry] = useState(0);
  const [look, setLook] = useState(null);
  const [isDev, setIsDev] = useState(Boolean(apiClient?.isDev));
  const [cohorts, setCohorts] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  const [cohortStats, setCohortStats] = useState({});

  useEffect(() => {
    const handleProfileUpdate = (client) => setIsDev(Boolean(client?.isDev));
    apiClient?.onProfileUpdated?.(handleProfileUpdate);
    handleProfileUpdate(apiClient);
    return () => apiClient?.offProfileUpdated?.(handleProfileUpdate);
  }, [apiClient]);

  // What the filters currently say. Nothing loads until it's applied, so the
  // dashboard never runs on filters the user hasn't finished choosing.
  const draft = useMemo(() => buildRequest({ range, emr, noteType, clinicId, emails, flag, flagState, source, multiRecord }),
    [range, emr, noteType, clinicId, emails, flag, flagState, source, multiRecord]);
  const [applied, setApplied] = useState(null);
  const request = applied;
  const dirty = !applied || JSON.stringify(draft) !== JSON.stringify(applied);
  // Card clicks are an explicit choice, so they apply straight away.
  const applyNow = (changes) => setApplied((current) => {
    const next = { ...(current || draft), ...changes };
    Object.keys(changes).forEach((key) => { if (!changes[key]) delete next[key]; });
    return next;
  });

  useEffect(() => {
    if (!emailFilterOpen) return undefined;
    const closeOnOutsideClick = (event) => {
      if (!emailFilterRef.current?.contains(event.target)) setEmailFilterOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutsideClick);
    return () => document.removeEventListener("pointerdown", closeOnOutsideClick);
  }, [emailFilterOpen]);

  useEffect(() => {
    if (!isDev) return undefined;
    let active = true;
    apiClient.JMPost("/v1/notes/feedback/filters", { ...range, userIds: [] }).then((response) => {
      if (active && !response?.error) setFilterOptions(response || {});
    }).catch(() => { /* dropdowns fall back to empty; the overview still loads */ });
    return () => { active = false; };
  }, [apiClient, range, isDev]);

  useEffect(() => {
    if (!isDev || !request) return undefined;
    let active = true;
    setOverview(null);
    setLoading(true);
    setError("");
    feedbackRead(apiClient, "/v1/notes/feedback/overview", request, () => active).then((response) => {
      if (!active) return;
      if (response?.error) throw new Error(response.error);
      setOverview(response);
    }).catch(() => {
      if (active) setError("Unable to load feedback. Try again.");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [apiClient, request, retry, isDev]);

  // Cohorts and chart notes are shared records, not filtered.
  useEffect(() => {
    if (!isDev) return undefined;
    let active = true;
    setCohorts([]);
    setAnnotations([]);
    apiClient.JMPost("/v1/notes/feedback/cohorts", {}).then((response) => {
      if (!active || response?.error) return;
      setCohorts(response.cohorts || []);
      setAnnotations(response.annotations || []);
    }).catch(() => { /* the chart still works for everyone */ });
    return () => { active = false; };
  }, [apiClient, isDev]);

  useEffect(() => {
    if (!isDev || !request || !cohorts.length) return undefined;
    let active = true;
    feedbackRead(apiClient, "/v1/notes/feedback/cohorts/stats", request, () => active).then((response) => {
      if (active && !response?.error) setCohortStats(response.stats || {});
    }).catch(() => { /* cards show no rate */ });
    return () => { active = false; };
  }, [apiClient, request, isDev, cohorts]);

  const saved = (promise, message) => promise.then((response) => {
    if (!response || response.error) throw new Error(response?.error || message);
    return response;
  }, () => { throw new Error(message); });

  const saveCohort = (name, emails) => saved(apiClient.JMPost("/v1/notes/feedback/cohorts/save", { name, emails }), "Unable to save the cohort.")
    .then((cohort) => setCohorts((current) => [...current, cohort]));

  const deleteCohort = (cohortId) => {
    const drop = () => {
      setCohorts((current) => current.filter((c) => c.cohortId !== cohortId));
      setAnnotations((current) => current.map((a) => (a.cohortId === cohortId ? { ...a, cohortId: null } : a)));
    };
    return saved(apiClient.JMPost("/v1/notes/feedback/cohorts/delete", { cohortId }), "Unable to remove the cohort.")
      .then(drop).catch((err) => window.alert(err.message));
  };

  const saveAnnotation = (date, label, cohortId) => saved(apiClient.JMPost("/v1/notes/feedback/annotations/save", { date, label, cohortId: cohortId || undefined }), "Unable to save the note.")
    .then((note) => setAnnotations((current) => [...current, note]));

  const deleteAnnotation = (annotationId) => {
    const drop = () => setAnnotations((current) => current.filter((a) => a.annotationId !== annotationId));
    return saved(apiClient.JMPost("/v1/notes/feedback/annotations/delete", { annotationId }), "Unable to remove the note.")
      .then(drop).catch((err) => window.alert(err.message));
  };

  if (!isDev) {
    return <Alert color="warning">Developer access is required.</Alert>;
  }

  const choices = filterOptions;
  const kpis = overview?.kpis;
  const considerations = [
    ...(overview?.considerations || []),
    ...(flag ? [{ kind: "flagFilter" }] : []),
    ...(DEFAULT_ON_FLAGS.includes(flag) ? [{ kind: "flagDefaultOn", flag }] : []),
  ].map(considerationText).filter(Boolean);
  const scopeLabel = `${overview ? `${shortDate(overview.range.startDate)} – ${shortDate(overview.range.endDate)}` : ""} · ${emr || "All EMRs"}`;

  const choosePreset = (next) => {
    setPreset(next.key);
    setRange(presetRange(next.days));
  };

  return (
    <div className="note-intel-dashboard fb-dashboard">
      <header className="note-intel-header">
        <div>
          <h2>Note feedback</h2>

        </div>
        <div className="fb-header-meta">
          <p className="fb-subtitle">{PRESETS.find((p) => p.key === preset)?.label || (preset === "day" ? "Selected day" : "Custom range")} · {emr || "All EMRs"}</p>
        </div>
      </header>

      <section className="note-intel-filter-panel fb-filters" aria-label="Filters">
        <div className="fb-filter-toolbar">
        <div className="fb-presets" role="group" aria-label="Date range">
          {PRESETS.map((item) => (
            <button type="button" key={item.key} aria-pressed={preset === item.key} onClick={() => choosePreset(item)}>
              {item.label}
            </button>
          ))}
          <button type="button" aria-pressed={preset === "day"} onClick={() => { setPreset("day"); setRange({ startDate: range.endDate, endDate: range.endDate }); }}>Pick a day</button>
          <button type="button" aria-pressed={preset === "custom"} onClick={() => setPreset("custom")}>Custom range</button>
        </div>
        {(preset === "day" || preset === "custom") && <div className="fb-date-inputs">
          {preset === "day" && <Input type="date" aria-label="Selected day" value={range.startDate}
            onChange={(event) => event.target.value && setRange({ startDate: event.target.value, endDate: event.target.value })} />}
          {preset === "custom" && <>
            <Input type="date" aria-label="Start date" value={range.startDate} max={range.endDate}
              onChange={(event) => event.target.value && setRange({ ...range, startDate: event.target.value })} />
            <Input type="date" aria-label="End date" value={range.endDate} min={range.startDate}
              onChange={(event) => event.target.value && setRange({ ...range, endDate: event.target.value })} />
          </>}
        </div>}
        <div className="fb-filter-grid">
          <FormGroup>
            <Label for="fb-emr">EMR</Label>
            <Input id="fb-emr" type="select" value={emr} onChange={(event) => setEmr(event.target.value)}>
              <option value="">All EMRs</option>
              {options(choices.emr).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Input>
          </FormGroup>
          <div className="form-group fb-clinician-filter" ref={emailFilterRef}
            onKeyDown={(event) => { if (event.key === "Escape") setEmailFilterOpen(false); }}>
            <button type="button" className="fb-email-trigger" aria-expanded={emailFilterOpen}
              onClick={() => setEmailFilterOpen((open) => !open)}>
              <span>Clinicians</span><strong>{draft.userIds.length ? `${draft.userIds.length} emails` : "All"}</strong>
            </button>
            {emailFilterOpen && <div className="fb-email-popover">
              <Label for="fb-user">Clinician emails</Label>
              <Input id="fb-user" type="textarea" rows={4} value={emails}
                placeholder="name@clinic.com, another@clinic.com" autoComplete="off"
                aria-label="Clinician emails, separated by commas or newlines"
                onChange={(event) => setEmails(event.target.value)} />
              <div className="fb-email-popover-footer">
                <span>Separate emails with commas or new lines.</span>
                <button type="button" onClick={() => setEmailFilterOpen(false)}>Done</button>
              </div>
            </div>}
          </div>
          <FormGroup>
            <Label for="fb-note-type">Visit type</Label>
            <Input id="fb-note-type" type="select" value={noteType} onChange={(event) => setNoteType(event.target.value)}>
              <option value="">All</option>
              {options(choices.noteType).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="fb-source">Source</Label>
            <Input id="fb-source" type="select" value={source} onChange={(event) => setSource(event.target.value)}>
              <option value="">All</option>
              {options(choices.source).map((option) => <option key={option.value} value={option.value}>{sourceLabel(option.value)}</option>)}
            </Input>
          </FormGroup>
          <FormGroup>
            <Label for="fb-multi-record">Multi-record</Label>
            <Input id="fb-multi-record" type="select" value={multiRecord} onChange={(event) => setMultiRecord(event.target.value)}>
              <option value="">All visits</option>
              {MULTI_RECORD_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
            </Input>
          </FormGroup>
          <FormGroup className="fb-feature-filter">
            <Label for="fb-flag">Feature flag</Label>
            <div className="fb-flag-row">
              <Input id="fb-flag" type="select" value={flag} onChange={(event) => setFlag(event.target.value)}>
                <option value="">All</option>
                {options(choices.flag).map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </Input>
              {flag && <Input type="select" aria-label="Flag state" value={flagState} onChange={(event) => setFlagState(event.target.value)}>
                <option value="on">On</option>
                <option value="off">Off</option>
              </Input>}
            </div>
          </FormGroup>
          <FormGroup>
            <Label for="fb-clinic">Clinic</Label>
            <Select inputId="fb-clinic" classNamePrefix="note-intel-select" isClearable placeholder="All"
              options={options(choices.clinic)}
              value={options(choices.clinic).find((option) => option.value === clinicId) || null}
              onChange={(option) => setClinicId(option?.value || "")} />
          </FormGroup>
        </div>
        </div>
        <div className="fb-apply-row">
          <Button color="primary" size="sm" disabled={!dirty} onClick={() => setApplied(draft)}>
            {applied ? "Apply filters" : "Show feedback"}
          </Button>
          {applied && dirty && <span className="fb-muted">Filters changed. Apply to update the dashboard.</span>}
        </div>
      </section>

      {!applied && (
        <p className="fb-muted fb-start-hint">Choose a date range and any filters, then click Show feedback.</p>
      )}

      {error && <Alert color="danger">{error} <Button size="sm" onClick={() => setRetry(retry + 1)}>Retry</Button></Alert>}
      {loading && !overview && <div className="fb-loading"><Spinner size="sm" /> Loading feedback…</div>}

      {overview && <section className="fb-section" aria-busy={loading}>
        <div className="fb-section-heading">
          <span className="fb-section-number">01</span>
          <h3>Overview</h3>
          <span className="fb-muted">The main takeaways for your filters</span>
        </div>

        <div className="note-intel-panel fb-panel fb-areas-panel">
          <div className="note-intel-panel-header">
            <div>
              <h3>Areas to look into</h3>
              <p>Where thumbs downs are rising or higher than other EMRs, ranked by how many notes it affects. Last 2 weeks vs the 2 weeks before, ending {shortDate(overview.trendWindow.endDate)}.</p>
            </div>
          </div>
          {overview.trendCards.length
            ? <div className="fb-card-grid">
                {overview.trendCards.map((card) => (
                  <TrendCard key={`${card.emr}:${card.section}`} card={card}
                    onLook={() => setLook({ ...card.look, title: `${card.section} on ${card.emr}` })} />
                ))}
              </div>
            : <p className="fb-muted">Nothing stands out for these filters. A card needs at least 3 thumbs downs behind it and a 2-point gap.</p>}
        </div>

        <div className="note-intel-panel fb-panel">
          <div className="note-intel-panel-header">
            <div>
              <h3>Why notes get a thumbs down, and where</h3>
              <p>Top 3 reasons PTs picked and the sections they flagged with them. Click one for the details and comments.</p>
            </div>
            <span className="fb-muted">Based on {count(kpis.downsWithReason)} thumbs downs with a reason · {scopeLabel}</span>
          </div>
          {overview.reasons.length
            ? <div className="fb-card-grid">
                {overview.reasons.map((reason, index) => (
                  <button type="button" key={reason.reason} className={`fb-reason-card${index === 0 ? " fb-reason-top" : ""}`}
                    onClick={() => setLook({ reason: reason.reason, title: reasonLabel(reason.reason) })}>
                    <span className="fb-rank">#{index + 1}{index === 0 ? " REASON" : ""}</span>
                    <span className="fb-reason-pct">{pct(reason.pct)}</span>
                    <strong>{reasonLabel(reason.reason)}</strong>
                    <span className="fb-muted">of thumbs downs with a reason</span>
                    {!!reason.sections.length && <span className="fb-reason-where">
                      mostly in {reason.sections.map((s) => <span key={s.section}><strong>{s.section}</strong> {pct(s.pct)}</span>)}
                    </span>}
                    <span className="fb-link">See details and comments ›</span>
                  </button>
                ))}
              </div>
            : <p className="fb-muted">No thumbs downs with a reason in these filters yet.</p>}
        </div>

        <div className="fb-kpi-row">
          <div className="fb-kpi">
            <span>Thumbs-down rate</span>
            <strong>{pct(kpis.downRate)}</strong>
            {kpis.downRateChange != null && <em className={kpis.downRateChange <= 0 ? "fb-good" : "fb-bad"}>
              {points(kpis.downRateChange)} vs the previous {overview.range.days} days
            </em>}
            <small>{count(kpis.downs)} thumbs downs · lower is better</small>
          </div>
          <div className="fb-kpi">
            <span>Thumbs-up rate</span>
            <strong>{pct(kpis.upRate)}</strong>
            <small>{count(kpis.ups)} thumbs ups · notes PTs were happy with</small>
          </div>
          <div className="fb-kpi">
            <span>Notes rated</span>
            <strong>{count(kpis.ratedNotes ?? kpis.rated)}</strong>
            {kpis.ratedNotes != null && kpis.rated !== kpis.ratedNotes && <small>{count(kpis.rated)} ratings (some notes were comprehended more than once)</small>}
            <small>{kpis.coverage == null
              ? "No captured notes from PTs with the rating bar yet"
              : `${pct(kpis.coverage)} of ${count(kpis.eligibleNotes)} captured notes from PTs with the rating bar`}</small>
          </div>
          <div className="fb-kpi">
            <span>Active users rating</span>
            <strong>{count(kpis.users)}</strong>
            <small>higher = rates are more trustworthy</small>
          </div>
        </div>

        {!!considerations.length && <div className="note-intel-panel fb-panel">
          <div className="note-intel-panel-header"><div><h3>Biases and things to consider</h3></div></div>
          <ul className="fb-considerations">
            {considerations.map(([tone, text]) => (
              <li key={text}><span className={`fb-tone ${tone === "Heads up" ? "fb-tone-warn" : ""}`}>{tone}</span>{text}</li>
            ))}
          </ul>
        </div>}
      </section>}

      {overview?.breakdown && <section className="fb-section" aria-busy={loading}>
        <div className="fb-section-heading">
          <span className="fb-section-number">02</span>
          <h3>What PTs are telling us</h3>
          <span className="fb-muted">Themes, reasons and the sections they flagged</span>
        </div>
        <FeedbackBreakdown breakdown={overview.breakdown} onLook={setLook} />
        <FeedbackThemes apiClient={apiClient} request={request} onLook={setLook} />
      </section>}

      {/* Not tied to the overview request, so its picks survive filter changes. */}
      {request && <section className="fb-section">
        <div className="fb-section-heading">
          <span className="fb-section-number">03</span>
          <h3>Trends and changes</h3>
          <span className="fb-muted">Is it getting better, and did our changes help?</span>
        </div>
        <FeedbackTrends apiClient={apiClient} request={request}
          cohorts={cohorts} annotations={annotations}
          sectionOptions={(overview?.breakdown?.sections || []).map((row) => row.section)} />
        <FeedbackCohorts cohorts={cohorts} annotations={annotations} cohortStats={cohortStats}
          onSaveCohort={saveCohort} onDeleteCohort={deleteCohort}
          onSaveAnnotation={saveAnnotation} onDeleteAnnotation={deleteAnnotation} />
      </section>}

      {request && <section className="fb-section">
        <div className="fb-section-heading">
          <span className="fb-section-number">04</span>
          <h3>Compare</h3>
          <span className="fb-muted">Visit types, EMRs and features side by side</span>
        </div>
        <FeedbackCompare apiClient={apiClient} request={request}
          noteType={noteType} emr={emr}
          onFilterNoteType={(value) => { setNoteType(value); applyNow({ noteType: value }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          onFilterEmr={(value) => { setEmr(value); applyNow({ emr: value }); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      </section>}

      {request && <section className="fb-section">
        <div className="fb-section-heading">
          <span className="fb-section-number">05</span>
          <h3>Accuracy check</h3>
          <span className="fb-muted">How reported feedback relates to captured note edits</span>
        </div>
        <FeedbackAccuracy apiClient={apiClient} request={request} />
      </section>}

      {request && <section className="fb-section">
        <div className="fb-section-heading">
          <span className="fb-section-number">06</span>
          <h3>Latest thumbs downs</h3>
          <span className="fb-muted">Open one to see exactly what happened</span>
        </div>
        <FeedbackLatest apiClient={apiClient} request={request} />
      </section>}

      {look && <FeedbackRatingsModal
        apiClient={apiClient} request={request} look={look} title={look.title}
        onClose={() => setLook(null)}
      />}
    </div>
  );
};

export default FeedbackDashboard;
